import React, { FC, useCallback, useMemo } from 'react';
import { useMatches } from 'react-router-dom';

import { IconType } from '@/assets/icons';
import { NavigationBarItemType } from '@/components/ui/NavigationBar/types';
import {
  SidebarEintrag,
  SidebarPfeil,
  SidebarUnterpunkt,
  SidebarUnterpunkte,
} from '@/components/ui/Rahmen';

export interface SideBarItemData {
  path: string;
  icon?: IconType;
  iconActive?: IconType;
  items?: SideBarItemData[];
  label?: string;
  /**
   * Nur fuer die echte Rolle `sa`, nicht fuer `unteradmin` (dessen Sidebar ist
   * die SA-Liste minus Bereiche). Beispiel: API-Schluessel (Decision #252, H).
   */
  nurEchterSA?: boolean;
}

export interface SideBarItemProps {
  item: SideBarItemData;
  active?: boolean;
  altColor?: boolean;
  isExpanded?: boolean;
  label?: string;
  subItems?: NavigationBarItemType[];
  isGroupExpanded?: boolean;
  onSideBarItemClick?: (item: SideBarItemData) => void;
  onToggleGroup?: () => void;
  onSubItemClick?: (subItemPath: string, mainItemIcon: string) => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave?: () => void;
}

const SideBarItem: FC<SideBarItemProps> = ({
  item,
  active,
  altColor,
  isExpanded,
  label,
  subItems = [],
  isGroupExpanded,
  onSideBarItemClick,
  onToggleGroup,
  onSubItemClick,
  onMouseEnter,
  onMouseLeave,
}) => {
  const matches = useMatches();
  const hasSubItems = subItems.length > 0;

  const handleClick = useCallback(() => {
    if (hasSubItems) {
      // Toggle group open/closed
      onToggleGroup?.();
      // Also scroll to section (useful in BeratungScrollView with disableNavigation)
      onSideBarItemClick?.(item);
    } else {
      onSideBarItemClick?.(item);
    }
  }, [item, hasSubItems, onSideBarItemClick, onToggleGroup]);

  const handleToggleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleGroup?.();
    },
    [onToggleGroup],
  );

  const handleSubItemClick = useCallback(
    (subItemPath: string) => {
      onSubItemClick?.(subItemPath, item.icon || '');
    },
    [onSubItemClick, item.icon],
  );

  const isSubItemActive = useCallback(
    (path: string) => {
      return matches.some(
        (match) =>
          match.pathname.endsWith(`/${path}`) ||
          match.pathname.includes(`/${path}/`),
      );
    },
    [matches],
  );

  const itemIcon = useMemo(() => {
    if (!!item.icon && !!item.iconActive) {
      return active ? item.iconActive : item.icon;
    }
    return item.icon;
  }, [active, item.icon, item.iconActive]);

  return (
    <SidebarEintrag
      icon={itemIcon}
      label={label}
      aktiv={active}
      ausgeklappt={isExpanded}
      altColor={altColor}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      pfeil={
        hasSubItems && (
          <SidebarPfeil offen={!!isGroupExpanded} onClick={handleToggleClick} />
        )
      }
    >
      {isExpanded && hasSubItems && (
        <SidebarUnterpunkte offen={!!isGroupExpanded}>
          {subItems.map((subItem, index) => (
            <SidebarUnterpunkt
              key={index}
              aktiv={isSubItemActive(subItem.path)}
              onClick={() => handleSubItemClick(subItem.path)}
            >
              {subItem.label}
            </SidebarUnterpunkt>
          ))}
        </SidebarUnterpunkte>
      )}
    </SidebarEintrag>
  );
};

export default SideBarItem;
